import { useQuery } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { GetThirdPartyContactsByThirdPartyIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

import { useGetThirdPartyContactsTRPC } from './useGetThirdPartyContactsTRPC';

export const useGetThirdPartyContacts = (
  thirdPartyId: string,
  includeRevoked = false,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const { addNotification } = useNotifications();

  const {
    data: graphqlData,
    loading: graphqlLoading,
    refetch: graphqlRefetch,
  } = useQuery(GetThirdPartyContactsByThirdPartyIdDocument, {
    variables: {
      ThirdPartyId: thirdPartyId,
    },
    fetchPolicy: 'no-cache',
    skip: trpcEnabled || skip, // Skip GraphQL query when TRPC is enabled
    onError: (error) => {
      if (!trpcEnabled) {
        addNotification({
          type: 'error',
          content: error.message,
        });
      }
    },
  });

  // Filter out revoked contacts if needed (GraphQL returns all, filtering done client-side)
  const filteredGraphqlData = graphqlData
    ? {
        ...graphqlData,
        third_party_contact: includeRevoked
          ? graphqlData.third_party_contact
          : graphqlData.third_party_contact.filter((c) => !c.IsRevoked),
      }
    : undefined;

  const {
    data: trpcData,
    loading: trpcLoading,
    refetch: trpcRefetch,
  } = useGetThirdPartyContactsTRPC(thirdPartyId, includeRevoked, skip);

  // Return appropriate data based on feature flag
  if (trpcEnabled) {
    return {
      loading: trpcLoading,
      data: trpcData,
      refetch: async () => {
        await trpcRefetch();
      },
    };
  }

  return {
    loading: graphqlLoading,
    data: filteredGraphqlData,
    refetch: async () => {
      await graphqlRefetch();
    },
  };
};
